import type { FeatureFlagRecord, FeatureFlagRegistry, FeatureFlagRegistryFactory } from '@/scopes/common/domain/types';
import type { FeatureFlagId } from '@/scopes/common/infrastructure/feature-flags/featureFlags';
import { ACTIVE_FEATURE_FLAGS, FEATURE_FLAGS } from '@/scopes/common/infrastructure/feature-flags/featureFlags';

const inMemoryFeatureFlagRegistry: FeatureFlagRegistryFactory = <T extends string>(
  record: FeatureFlagRecord<T>,
  activeList: T[] = []
): FeatureFlagRegistry<T> => {
  return {
    activate(id): FeatureFlagRegistry<T> {
      return inMemoryFeatureFlagRegistry<T>(record, [...activeList, id]);
    },
    deactivate(id): FeatureFlagRegistry<T> {
      return inMemoryFeatureFlagRegistry<T>(
        record,
        activeList.filter((activeId) => activeId !== id)
      );
    },
    getAll(): FeatureFlagRecord<T> {
      return record;
    },
    getActiveIdList(): T[] {
      return activeList;
    },
    isActive(id: T): boolean {
      return activeList.includes(id);
    },
  };
};

export function createInMemoryFeatureFlagRegistry<T extends string = FeatureFlagId>({
  initialFlags = FEATURE_FLAGS as FeatureFlagRecord<T>,
  activeList = ACTIVE_FEATURE_FLAGS as T[],
  commaSeparatedActiveList,
}: {
  initialFlags: FeatureFlagRecord<T>;
  activeList: T[];
  commaSeparatedActiveList?: string;
}): FeatureFlagRegistry<T> {
  return inMemoryFeatureFlagRegistry<T>(
    initialFlags,
    filterInvalidFlagIds<T>(
      [...activeList, ...extractActiveFeatureFlagsFrom<T>(commaSeparatedActiveList || getActiveFeatureFlags())],
      initialFlags
    )
  );
}

function extractActiveFeatureFlagsFrom<T>(commaSeparatedActiveFFList: string): T[] {
  return commaSeparatedActiveFFList.split(',') as T[];
}

function filterInvalidFlagIds<T extends string>(flagIds: T[], initialFlags: FeatureFlagRecord<T>): T[] {
  return flagIds.filter((id) => (id as string) in initialFlags);
}

export default createInMemoryFeatureFlagRegistry({
  initialFlags: FEATURE_FLAGS,
  activeList: ACTIVE_FEATURE_FLAGS,
});

export function getActiveFeatureFlags() {
  return process.env.NEXT_PUBLIC_ACTIVE_FEATURE_FLAGS || '';
}
